/**
 * Collection Tabs Swiper
 * Handles tab switching and carousel functionality
 */

(function() {
  'use strict';

  // Global storage for Swiper instances
  const swiperInstances = new Map();

  /**
   * CollectionTabsSwiper Class
   */
  class CollectionTabsSwiper {
    constructor(sectionElement) {
      this.section = sectionElement;
      this.sectionId = sectionElement.getAttribute('data-section-id');

      // Product limit settings
      this.productsPerTabMobile = parseInt(this.section.getAttribute('data-products-mobile')) || 6;
      this.productsPerTabDesktop = parseInt(this.section.getAttribute('data-products-desktop')) || 8;

      // Elements
      this.tabButtons = this.section.querySelectorAll('.tab-button');
      this.tabContents = this.section.querySelectorAll('.tab-content');
      this.tabsNavigation = this.section.querySelector('[data-tabs-swiper]');

      // Active tab tracking
      this.activeTabIndex = 0;

      // Initialize
      this.init();
    }

    /**
     * Initialize the component
     */
    init() {
      if (!this.section || !this.tabButtons.length) return;

      // Initialize Tab Navigation Swiper
      this.initTabsSwiper();

      // Initialize Product Carousel for first tab
      this.initProductCarousel(this.activeTabIndex);

      // Bind tab click events
      this.bindEvents();
    }

    /**
     * Initialize Tab Navigation Swiper
     */
    initTabsSwiper() {
      if (!this.tabsNavigation) return;

      const tabsSwiperKey = this.sectionId + '-tabs';

      // Destroy existing instance if present
      if (swiperInstances.has(tabsSwiperKey)) {
        swiperInstances.get(tabsSwiperKey).destroy();
      }

      // Get navigation buttons
      const prevButton = this.section.querySelector('.tabs-nav-prev');
      const nextButton = this.section.querySelector('.tabs-nav-next');

      // Initialize Swiper for tabs (no navigation binding to Swiper)
      const tabsSwiper = new Swiper(this.tabsNavigation, {
        slidesPerView: 'auto',
        spaceBetween: 12,
        freeMode: true,
        watchSlidesProgress: true,

        // Fix for dragging in gaps between later tabs
        // See: https://github.com/nolimits4web/swiper/issues/4942
        touchEventsTarget: 'container',

        breakpoints: {
          768: {
            spaceBetween: 12
          },
          320: {
            spaceBetween: 8
          }
        }
      });

      // Store reference for use in other methods
      this.tabsSwiper = tabsSwiper;

      // Bind prev/next buttons to tab switching (not Swiper navigation)
      if (prevButton && nextButton) {
        prevButton.addEventListener('click', () => {
          const prevIndex = this.activeTabIndex - 1;
          if (prevIndex >= 0) {
            this.switchTab(prevIndex);
          }
        });

        nextButton.addEventListener('click', () => {
          const nextIndex = this.activeTabIndex + 1;
          if (nextIndex < this.tabButtons.length) {
            this.switchTab(nextIndex);
          }
        });

        // Update button visibility on init and resize
        const updateButtonVisibility = () => {
          updateNavigationButtonStates(
            tabsSwiper,
            prevButton,
            nextButton,
            this.activeTabIndex,
            this.tabButtons.length
          );
        };

        updateButtonVisibility();
        window.addEventListener('resize', updateButtonVisibility);
      }

      /**
       * Update navigation button states
       * Buttons control tab switching, not Swiper scrolling
       */
      function updateNavigationButtonStates(swiper, prevBtn, nextBtn, currentIndex, totalTabs) {
        if (!prevBtn || !nextBtn) return;

        // Check if tabs are overflowing (need navigation)
        const isOverflowing = swiper.slides.length > 0 && !swiper.isLocked;

        // If not overflowing, hide both buttons
        if (!isOverflowing) {
          prevBtn.style.display = 'none';
          nextBtn.style.display = 'none';
          return;
        }

        // Show/hide prev button based on current tab index
        if (currentIndex === 0) {
          prevBtn.style.display = 'none';
        } else {
          prevBtn.style.display = 'flex';
        }

        // Show/hide next button based on current tab index
        if (currentIndex === totalTabs - 1) {
          nextBtn.style.display = 'none';
        } else {
          nextBtn.style.display = 'flex';
        }
      }

      swiperInstances.set(tabsSwiperKey, tabsSwiper);
    }

    /**
     * Initialize Product Carousel for a specific tab
     * @param {Number} tabIndex - Index of the tab
     */
    initProductCarousel(tabIndex) {
      console.log(`[DEBUG] ========== initProductCarousel START (tabIndex: ${tabIndex}) ==========`);

      const tabContent = this.tabContents[tabIndex];
      if (!tabContent) {
        console.error(`[DEBUG] Tab content not found for index ${tabIndex}`);
        return;
      }

      const carouselElement = tabContent.querySelector('[data-products-swiper]');
      if (!carouselElement) {
        console.error(`[DEBUG] Carousel element not found in tab ${tabIndex}`);
        return;
      }

      console.log(`[DEBUG] Carousel element found:`, carouselElement);

      const carouselKey = this.sectionId + '-carousel-' + tabIndex;
      console.log(`[DEBUG] Carousel key: ${carouselKey}`);

      // Destroy existing instance if present
      if (swiperInstances.has(carouselKey)) {
        console.log(`[DEBUG] Destroying existing Swiper instance: ${carouselKey}`);
        swiperInstances.get(carouselKey).destroy();
      }

      /**
       * Check if should use mobile 2-row static grid
       * @returns {boolean}
       */
      function shouldUseMobileGrid() {
        const viewportWidth = window.innerWidth;
        const isMobile = viewportWidth <= 768;
        // Only count visible products (exclude .mobile-hidden-product on mobile)
        const visibleSlides = carouselElement.querySelectorAll('.swiper-slide:not(.mobile-hidden-product)');
        const visibleProductCount = visibleSlides.length;

        console.log(`[DEBUG] shouldUseMobileGrid check:`);
        console.log(`  - Viewport width: ${viewportWidth}px`);
        console.log(`  - isMobile (width <= 768): ${isMobile}`);
        console.log(`  - Visible products: ${visibleProductCount}`);
        console.log(`  - Threshold: 6`);
        console.log(`  - Will use mobile grid: ${isMobile && visibleProductCount > 6}`);

        // Use static grid if mobile and more than 6 visible products
        return isMobile && visibleProductCount > 6;
      }

      /**
       * Setup mobile 2-row layout with independent Swipers
       */
      function setupMobileTwoRowSwiper() {
        console.log(`[DEBUG] ========== setupMobileTwoRowSwiper START ==========`);

        const wrapper = carouselElement.querySelector('.swiper-wrapper');
        const visibleSlides = Array.from(carouselElement.querySelectorAll('.swiper-slide:not(.mobile-hidden-product)'));
        const productCount = visibleSlides.length;

        console.log(`[DEBUG] Mobile 2-Row setup:`);
        console.log(`  - Wrapper found:`, wrapper);
        console.log(`  - Visible slides count: ${productCount}`);
        console.log(`  - Visible slides:`, visibleSlides);

        if (!wrapper || productCount === 0) {
          console.error('[DEBUG ERROR] Wrapper not found or no products! Aborting mobile 2-row setup.');
          return null;
        }

        // Split products into 2 rows
        const productsPerRow = Math.ceil(productCount / 2);
        const row1Products = visibleSlides.slice(0, productsPerRow);
        const row2Products = visibleSlides.slice(productsPerRow);

        console.log(`[DEBUG] Products split:`);
        console.log(`  - Products per row: ${productsPerRow}`);
        console.log(`  - Row 1: ${row1Products.length} products`);
        console.log(`  - Row 2: ${row2Products.length} products`);

        // Create container for 2 rows
        const twoRowContainer = document.createElement('div');
        twoRowContainer.className = 'mobile-two-row-container';
        console.log(`[DEBUG] Created twoRowContainer:`, twoRowContainer);

        // Create Row 1 Swiper
        const row1Container = document.createElement('div');
        row1Container.className = 'products-swiper swiper mobile-row-swiper';
        row1Container.innerHTML = '<div class="swiper-wrapper"></div>';
        const row1Wrapper = row1Container.querySelector('.swiper-wrapper');
        row1Products.forEach(slide => row1Wrapper.appendChild(slide.cloneNode(true)));
        console.log(`[DEBUG] Row 1 container created with ${row1Products.length} slides`);

        // Create Row 2 Swiper
        const row2Container = document.createElement('div');
        row2Container.className = 'products-swiper swiper mobile-row-swiper';
        row2Container.innerHTML = '<div class="swiper-wrapper"></div>';
        const row2Wrapper = row2Container.querySelector('.swiper-wrapper');
        row2Products.forEach(slide => row2Wrapper.appendChild(slide.cloneNode(true)));
        console.log(`[DEBUG] Row 2 container created with ${row2Products.length} slides`);

        // Replace original swiper with 2-row container
        twoRowContainer.appendChild(row1Container);
        twoRowContainer.appendChild(row2Container);
        carouselElement.innerHTML = '';
        carouselElement.appendChild(twoRowContainer);

        console.log(`[DEBUG] DOM replaced - original carousel now contains twoRowContainer`);
        console.log(`[DEBUG] CarouselElement HTML:`, carouselElement.innerHTML.substring(0, 300) + '...');

        // Track initialization status - only show container when BOTH Swipers are ready
        let initializedCount = 0;
        const totalSwipers = 2;

        function checkBothInitialized() {
          initializedCount++;
          console.log(`[DEBUG] ✓ Swiper initialized (${initializedCount}/${totalSwipers})`);

          if (initializedCount === totalSwipers) {
            // Both Swipers are ready - show the container
            console.log(`[DEBUG] Both Swipers ready - adding 'initialized' class...`);
            requestAnimationFrame(() => {
              twoRowContainer.classList.add('initialized');

              // CRITICAL: Also show the parent carouselElement since it won't be Swiper-initialized
              // The carouselElement has .products-swiper which is hidden by default
              // and only shown when .swiper-initialized is present, but we're not initializing
              // Swiper on it (we're using the 2-row layout instead)
              carouselElement.style.opacity = '1';
              carouselElement.style.visibility = 'visible';

              console.log(`[DEBUG] ✓✓✓ 'initialized' class added to twoRowContainer`);
              console.log(`[DEBUG] ✓✓✓ carouselElement manually shown`);
              console.log(`[DEBUG] twoRowContainer classes:`, twoRowContainer.className);
              console.log(`[DEBUG] twoRowContainer computed style:`, {
                opacity: window.getComputedStyle(twoRowContainer).opacity,
                visibility: window.getComputedStyle(twoRowContainer).visibility,
                display: window.getComputedStyle(twoRowContainer).display
              });
              console.log(`[DEBUG] carouselElement computed style:`, {
                opacity: window.getComputedStyle(carouselElement).opacity,
                visibility: window.getComputedStyle(carouselElement).visibility,
                display: window.getComputedStyle(carouselElement).display
              });
            });
          }
        }

        // Full Swiper configuration with all effects
        const swiperConfig = {
          slidesPerView: 1.15,
          spaceBetween: 16,
          watchSlidesProgress: true,
          observer: true,
          observeParents: true,
          centeredSlides: false,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,

          // Enable mouse/trackpad dragging
          simulateTouch: true,
          allowTouchMove: true,
          touchRatio: 1,
          resistance: true,
          resistanceRatio: 0.85,

          // Fix for dragging in gaps between later slides
          // See: https://github.com/nolimits4web/swiper/issues/4942
          touchEventsTarget: 'container',

          // Enable slide tracking for peek-through effect
          watchSlidesVisibility: true,

          // Lazy loading
          lazy: {
            loadPrevNext: true,
            loadPrevNextAmount: 2
          },

          // Smooth transitions
          speed: 600,

          // Enable GPU acceleration
          cssMode: false,

          // Event callbacks for peek-through effect
          on: {
            init: function() {
              console.log(`[DEBUG] Swiper init callback fired for:`, this.el);
              updateCardVisibility(this);
              // CRITICAL: Only show container after BOTH Swipers are initialized
              checkBothInitialized();
            },
            slideChange: function() {
              updateCardVisibility(this);
            },
            setTranslate: function() {
              updateCardVisibility(this);
            },
            resize: function() {
              updateCardVisibility(this);
            }
          }
        };

        console.log(`[DEBUG] Swiper config created:`, swiperConfig);

        // Initialize both Swipers independently with full config
        console.log(`[DEBUG] Initializing Row 1 Swiper...`);
        const row1Swiper = new Swiper(row1Container, swiperConfig);
        console.log(`[DEBUG] Row 1 Swiper created:`, row1Swiper);

        console.log(`[DEBUG] Initializing Row 2 Swiper...`);
        const row2Swiper = new Swiper(row2Container, swiperConfig);
        console.log(`[DEBUG] Row 2 Swiper created:`, row2Swiper);

        console.log(`[DEBUG] Both Swiper objects created - waiting for init callbacks...`);

        // Store instances
        swiperInstances.set(carouselKey + '-row1', row1Swiper);
        swiperInstances.set(carouselKey + '-row2', row2Swiper);

        console.log(`[DEBUG] Swiper instances stored in map`);
        console.log(`[DEBUG] ========== setupMobileTwoRowSwiper END ==========`);

        return null; // Don't initialize main Swiper
      }

      /**
       * Setup mobile static grid layout (2 columns, multiple rows, vertical scroll)
       */
      function setupMobileGridLayout() {
        const wrapper = carouselElement.querySelector('.swiper-wrapper');
        // Only count visible slides (exclude .mobile-hidden-product)
        const visibleSlides = carouselElement.querySelectorAll('.swiper-slide:not(.mobile-hidden-product)');
        const productCount = visibleSlides.length;

        console.log(`[Mobile Grid] Total visible products: ${productCount}`);
        console.log(`[Mobile Grid] Wrapper element:`, wrapper);

        if (!wrapper) {
          console.error('[Mobile Grid] Wrapper not found!');
          return null;
        }

        // Mobile always uses 2 columns
        const columnsCount = 2;

        // Add grid class to wrapper
        wrapper.classList.add('mobile-grid-layout');
        wrapper.style.setProperty('--products-per-row', columnsCount);

        console.log(`[Mobile Grid] Set to ${columnsCount} columns`);
        console.log(`[Mobile Grid] Wrapper classes:`, wrapper.className);

        // Hide navigation buttons on mobile grid
        const prevBtn = carouselElement.querySelector('.swiper-button-prev');
        const nextBtn = carouselElement.querySelector('.swiper-button-next');
        if (prevBtn) prevBtn.style.display = 'none';
        if (nextBtn) nextBtn.style.display = 'none';

        // Don't initialize Swiper
        return null;
      }

      /**
       * Update card visibility based on slide progress
       * Creates smooth peek-through effect for edge cards
       */
      function updateCardVisibility(swiper) {
        if (!swiper || !swiper.slides) return;

        // Debug log
        console.log('[Peek-through] Updating card visibility, slides:', swiper.slides.length);

        // Get active index and slides per view
        const activeIndex = swiper.activeIndex;
        const slidesPerView = swiper.params.slidesPerView;

        // Calculate how many slides are fully visible
        const fullyVisibleSlides = Math.floor(slidesPerView);

        console.log('[Peek-through] Active index:', activeIndex, 'Slides per view:', slidesPerView);

        swiper.slides.forEach((slide, index) => {
          const card = slide.querySelector('.product-card-carousel');
          if (!card) return;

          // Calculate if this slide is in the main visible area
          const isFullyVisible = index >= activeIndex && index < activeIndex + fullyVisibleSlides;

          // Get slide progress for fine-tuning
          const progress = slide.progress || 0;
          const absProgress = Math.abs(progress);

          let opacity = 1;
          let scale = 1;

          // Only apply fade to slides outside the main visible area
          if (!isFullyVisible) {
            // Check if this is a peek-through slide (partially visible)
            const isPeekThrough = (index === activeIndex - 1) ||
                                  (index === activeIndex + fullyVisibleSlides);

            if (isPeekThrough) {
              // Peek-through slides: moderate fade
              opacity = 0.6;
              scale = 0.98;
            } else {
              // Completely outside: strong fade
              opacity = 0.65;
              scale = 0.96;
            }
          }

          // Debug log
          if (!isFullyVisible) {
            console.log(`[Peek-through] Card ${index}: isFullyVisible=${isFullyVisible}, progress=${progress.toFixed(2)}, opacity=${opacity.toFixed(2)}, scale=${scale.toFixed(3)}`);
          }

          // Apply styles with smooth transitions
          card.style.opacity = opacity.toFixed(2);
          card.style.transform = `scale(${scale.toFixed(3)})`;

          // Store scale in CSS variable for hover effect
          card.style.setProperty('--card-scale', scale.toFixed(3));
        });
      }

      // Check if should use mobile 2-row Swiper layout instead of single Swiper
      const useMobileGrid = shouldUseMobileGrid();
      console.log(`[DEBUG] Decision: useMobileGrid = ${useMobileGrid}`);

      if (useMobileGrid) {
        console.log('[DEBUG] ===>>> Calling setupMobileTwoRowSwiper()');
        setupMobileTwoRowSwiper();
        console.log('[DEBUG] ===>>> Exiting initProductCarousel - mobile 2-row setup complete');
        return; // Don't initialize main Swiper
      }

      console.log('[DEBUG] ===>>> Using standard desktop Swiper (not mobile 2-row)');

      // Swiper configuration
      const config = {
        slidesPerView: 1.2,
        spaceBetween: 12,
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        centeredSlides: false,

        // Enable mouse/trackpad dragging for desktop
        // Note: MacBook two-finger trackpad swipe is not natively supported by Swiper
        // Users can use mouse click-drag or navigation buttons instead
        simulateTouch: true,
        allowTouchMove: true,
        touchRatio: 1,
        resistance: true,
        resistanceRatio: 0.85,

        // Fix for dragging in gaps between later slides
        // See: https://github.com/nolimits4web/swiper/issues/4942
        touchEventsTarget: 'container',

        // Enable slide tracking for peek-through effect
        watchSlidesVisibility: true,

        // Navigation
        navigation: {
          nextEl: carouselElement.querySelector('.swiper-button-next'),
          prevEl: carouselElement.querySelector('.swiper-button-prev'),
        },

        // Lazy loading
        lazy: {
          loadPrevNext: true,
          loadPrevNextAmount: 2
        },

        // Smooth transitions
        speed: 600,

        // Enable GPU acceleration
        cssMode: false,

        // Event callbacks for peek-through effect
        on: {
          init: function() {
            updateCardVisibility(this);
          },
          slideChange: function() {
            updateCardVisibility(this);
          },
          setTranslate: function() {
            updateCardVisibility(this);
          },
          resize: function() {
            updateCardVisibility(this);
          }
        },

                // Breakpoints for responsive design
        breakpoints: {
          // Mobile (<768px): 1 slide + peek on right only
          320: {
            slidesPerView: 1.15,
            spaceBetween: 16,
            centeredSlides: false,
            slidesOffsetBefore: 0,
            slidesOffsetAfter: 0
          },
          // Tablet (768px - 1024px): 2.5 slides
          768: {
            slidesPerView: 2.5,
            spaceBetween: 16,
            centeredSlides: false
          },
          // laptop (>1024px): 3.2 slides
          1025: {
            slidesPerView: 3.2,
            spaceBetween: 20,
            centeredSlides: false
          },
          // laptop (>1400px): 3.5 slides
          1400: {
            slidesPerView: 3.5,
            spaceBetween: 20,
            centeredSlides: false
          },
          // Desktop (>1600px): 4 slides
          1600: {
            slidesPerView: 4,
            spaceBetween: 20,
            centeredSlides: false
          }
        }
      };

      // Initialize Swiper (only if not using mobile grid)
      console.log(`[DEBUG] Initializing desktop Swiper...`);
      const productSwiper = new Swiper(carouselElement, config);
      console.log(`[DEBUG] Desktop Swiper created:`, productSwiper);
      swiperInstances.set(carouselKey, productSwiper);
      console.log(`[DEBUG] Desktop Swiper instance stored with key: ${carouselKey}`);

      /**
       * Update navigation button visibility based on slide count and viewport
       */
      function updateNavigationButtonVisibility() {
        const prevBtn = carouselElement.querySelector('.swiper-button-prev');
        const nextBtn = carouselElement.querySelector('.swiper-button-next');

        if (!prevBtn || !nextBtn || !productSwiper) return;

        // Get total visible slides (exclude hidden ones)
        const totalSlides = productSwiper.slides.filter(slide =>
          !slide.classList.contains('mobile-hidden-product') &&
          !slide.classList.contains('desktop-hidden-product')
        ).length;

        // Get current slidesPerView based on viewport
        const currentSlidesPerView = productSwiper.params.slidesPerView;

        console.log(`[Nav Buttons] Total slides: ${totalSlides}, SlidesPerView: ${currentSlidesPerView}`);

        // Hide buttons if all slides fit in view (no need to navigate)
        // Use floor() because 3.2 means only 3 slides are fully visible
        if (totalSlides <= Math.floor(currentSlidesPerView)) {
          // Use setProperty with 'important' to override CSS
          prevBtn.style.setProperty('display', 'none', 'important');
          nextBtn.style.setProperty('display', 'none', 'important');
          console.log(`[Nav Buttons] Hidden - all slides fully visible`);
        } else {
          // Remove the inline style to let CSS take over
          prevBtn.style.removeProperty('display');
          nextBtn.style.removeProperty('display');
          console.log(`[Nav Buttons] Shown - slides need scrolling`);
        }
      }

      // Update on init
      updateNavigationButtonVisibility();

      // Update on resize
      window.addEventListener('resize', updateNavigationButtonVisibility);

      // Store the cleanup function
      productSwiper.navigationVisibilityCleanup = () => {
        window.removeEventListener('resize', updateNavigationButtonVisibility);
      };

      console.log(`[DEBUG] ========== initProductCarousel END (Desktop Swiper initialized) ==========`);
    }

    /**
     * Bind event listeners
     */
    bindEvents() {
      // Tab button clicks
      this.tabButtons.forEach((button, index) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.switchTab(index);
        });
      });
    }

    /**
     * Switch to a different tab
     * @param {Number} newIndex - Index of the tab to switch to
     */
    switchTab(newIndex) {
      if (newIndex === this.activeTabIndex) return;

      // Update active tab buttons
      this.tabButtons.forEach((btn, idx) => {
        if (idx === newIndex) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });

      // Update active tab content
      this.tabContents.forEach((content, idx) => {
        if (idx === newIndex) {
          content.classList.add('active');
        } else {
          content.classList.remove('active');
        }
      });

      // Initialize carousel for new tab if not already initialized
      const carouselKey = this.sectionId + '-carousel-' + newIndex;
      if (!swiperInstances.has(carouselKey)) {
        this.initProductCarousel(newIndex);
      }

      // Update active index
      this.activeTabIndex = newIndex;

      // Scroll tab navigation to show the active tab
      if (this.tabsSwiper) {
        this.tabsSwiper.slideTo(newIndex, 300);
      }

      // Update prev/next button visibility
      const prevButton = this.section.querySelector('.tabs-nav-prev');
      const nextButton = this.section.querySelector('.tabs-nav-next');
      if (prevButton && nextButton) {
        this.updateTabNavigationButtons(prevButton, nextButton);
      }
    }

    /**
     * Update tab navigation button visibility
     */
    updateTabNavigationButtons(prevBtn, nextBtn) {
      if (!prevBtn || !nextBtn || !this.tabsSwiper) return;

      // Check if tabs are overflowing
      const isOverflowing = this.tabsSwiper.slides.length > 0 && !this.tabsSwiper.isLocked;

      // If not overflowing, hide both buttons
      if (!isOverflowing) {
        prevBtn.style.display = 'none';
        nextBtn.style.display = 'none';
        return;
      }

      // Show/hide prev button based on current tab index
      if (this.activeTabIndex === 0) {
        prevBtn.style.display = 'none';
      } else {
        prevBtn.style.display = 'flex';
      }

      // Show/hide next button based on current tab index
      if (this.activeTabIndex === this.tabButtons.length - 1) {
        nextBtn.style.display = 'none';
      } else {
        nextBtn.style.display = 'flex';
      }
    }

    /**
     * Destroy all Swiper instances for this section
     */
    destroy() {
      // Destroy tab navigation swiper
      const tabsSwiperKey = this.sectionId + '-tabs';
      if (swiperInstances.has(tabsSwiperKey)) {
        swiperInstances.get(tabsSwiperKey).destroy();
        swiperInstances.delete(tabsSwiperKey);
      }

      // Destroy all product carousel swipers
      this.tabContents.forEach((_, index) => {
        const carouselKey = this.sectionId + '-carousel-' + index;
        if (swiperInstances.has(carouselKey)) {
          swiperInstances.get(carouselKey).destroy();
          swiperInstances.delete(carouselKey);
        }
      });
    }
  }

  /**
   * Initialize section
   * @param {HTMLElement} section - Section element
   */
  function initSection(section) {
    if (!section) return;

    const sectionId = section.getAttribute('data-section-id');

    // Store instance for potential cleanup
    const instance = new CollectionTabsSwiper(section);

    // Store reference for Shopify theme editor
    section._collectionTabsSwiper = instance;
  }

  /**
   * Shopify Theme Editor: Section Load Event
   */
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target.querySelector('[data-section-type="collection-tabs-swiper"]');
    if (section) {
      // Destroy previous instance if exists
      if (section._collectionTabsSwiper) {
        section._collectionTabsSwiper.destroy();
      }
      // Initialize new instance
      initSection(section);
    }
  });

  /**
   * Shopify Theme Editor: Section Unload Event
   */
  document.addEventListener('shopify:section:unload', function(event) {
    const section = event.target.querySelector('[data-section-type="collection-tabs-swiper"]');
    if (section && section._collectionTabsSwiper) {
      section._collectionTabsSwiper.destroy();
    }
  });

  /**
   * Shopify Theme Editor: Block Select Event
   */
  document.addEventListener('shopify:block:select', function(event) {
    const section = event.target.closest('[data-section-type="collection-tabs-swiper"]');
    if (!section || !section._collectionTabsSwiper) return;

    const blockId = event.detail.blockId || event.target.getAttribute('data-block-id');
    const instance = section._collectionTabsSwiper;

    // Find and activate the corresponding tab
    instance.tabButtons.forEach((button, index) => {
      if (button.getAttribute('data-block-id') === blockId) {
        instance.switchTab(index);
      }
    });
  });

  /**
   * Initialize on DOM ready
   */
  function initOnReady() {
    const sections = document.querySelectorAll('[data-section-type="collection-tabs-swiper"]');
    sections.forEach(initSection);
  }

  // Check if DOM is already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOnReady);
  } else {
    initOnReady();
  }

  /**
   * Intersection Observer for lazy loading optimization
   * Only initialize Swiper when section is near viewport
   */
  if ('IntersectionObserver' in window) {
    const observerOptions = {
      root: null,
      rootMargin: '200px',
      threshold: 0
    };

    const sectionObserver = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting && !entry.target._collectionTabsSwiper) {
          initSection(entry.target);
          sectionObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe sections on page load
    document.addEventListener('DOMContentLoaded', function() {
      const sections = document.querySelectorAll('[data-section-type="collection-tabs-swiper"]');
      sections.forEach(function(section) {
        sectionObserver.observe(section);
      });
    });
  }

})();
